import { ConvexAuthProvider } from '@convex-dev/auth/react'
import { ConvexReactClient } from 'convex/react'
import React from 'react'
import { isRouteErrorResponse, Link, Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData } from 'react-router'
import { ToastProvider } from '@repo/ui/components/toast'

import { ColdStartBanner } from './components/ColdStartBanner'
import { getPublicEnv } from './utils/env.server.ts'

import appCss from './styles.css?url'

const THEME_SCRIPT = `(function(){var t=localStorage.getItem('pref:theme')||'system';if(t==='dark'||(t==='system'&&matchMedia('(prefers-color-scheme:dark)').matches))document.documentElement.classList.add('dark');})();`

export const links = () => [
	{ rel: 'stylesheet', href: appCss },
	{ rel: 'icon', href: '/favicon.ico', sizes: '48x48' },
	{ rel: 'icon', href: '/static/favicons/favicon.svg', type: 'image/svg+xml' },
	{ rel: 'icon', href: '/static/favicons/favicon-96x96.png', type: 'image/png', sizes: '96x96' },
	{ rel: 'apple-touch-icon', href: '/static/favicons/apple-touch-icon.png' },
	{ rel: 'manifest', href: '/site.webmanifest' },
]

export const meta = () => [
	{ charSet: 'utf-8' },
	{ name: 'viewport', content: 'width=device-width, initial-scale=1' },
	{ title: 'HearSay' },
]

export async function loader() {
	const ENV = getPublicEnv()

	// Fire-and-forget: wake the API so it's ready by the time the user needs it
	if (ENV.API_URL) {
		fetch(`${ENV.API_URL}/api/wake`).catch(() => {})
	}

	return { ENV }
}

export function Layout({ children }: { children: React.ReactNode }) {
	return (
		<html lang="en">
			<head>
				<script dangerouslySetInnerHTML={{ __html: THEME_SCRIPT }} />
				<Meta />
				<Links />
			</head>
			<body className="bg-background min-h-screen antialiased">
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	)
}

export default function Root() {
	const { ENV } = useLoaderData<typeof loader>()

	const [convex] = React.useState(() => new ConvexReactClient(ENV.CONVEX_URL!))

	return (
		<>
			<script dangerouslySetInnerHTML={{ __html: `window.ENV = ${JSON.stringify(ENV)}` }} />
			<ConvexAuthProvider client={convex}>
				<ToastProvider>
					<ColdStartBanner />
					<Outlet />
				</ToastProvider>
			</ConvexAuthProvider>
		</>
	)
}

export function ErrorBoundary({ error }: { error: unknown }) {
	if (isRouteErrorResponse(error) && error.status === 404) {
		return (
			<main className="flex min-h-screen flex-col items-center justify-center gap-4">
				<p className="text-muted-foreground">Page not found.</p>
				<Link to="/" className="text-sm underline-offset-4 hover:underline">
					Go home
				</Link>
			</main>
		)
	}

	return (
		<main className="flex min-h-screen flex-col items-center justify-center gap-4">
			<p className="text-muted-foreground">Something went wrong.</p>
			<Link to="/" className="text-sm underline-offset-4 hover:underline">
				Go home
			</Link>
		</main>
	)
}
